import * as _ from 'lodash';
import LRU from 'lru-cache';

import * as OpTypes from '../opTypes';
import * as OpKinds from '../opKinds';
import * as Graph from '../graph';
import * as Types from '../model/types';
import {notEmpty} from '../utils/obj';

export const escapeDots = (s: string) => {
  return s.replace(new RegExp('\\.', 'g'), '\\.');
};

export const unEscapeDots = (s: string) => {
  return s.replace(/\\\./g, '.');
};

// This function is called in the opPick resolver, for the key passed to op
// opPick. opPick tends to be called many times with the same key, for example
// with filter and groupBy operations. So we manually memoize this function here,
// but bound cached results to 8MB
const splitEscapedStringCache = new LRU<string, string[]>({
  max: 1048576 * 8,
  length: val => {
    return val.reduce((len, el) => {
      return len + el.length;
    }, 0);
  },
  updateAgeOnGet: true,
});
export const splitEscapedString = (s: string) => {
  const cached = splitEscapedStringCache.get(s);
  if (typeof cached !== 'undefined') {
    return cached;
  }

  const placeholder = '__wb_escaped_sep__';
  const result = s
    .replace(new RegExp('\\\\.', 'g'), placeholder)
    .split('.')
    .map(is => is.replace(new RegExp(placeholder, 'g'), '.'));
  splitEscapedStringCache.set(s, result);
  return result;
};

const makeObjectOp = OpKinds.makeStandardOp;

const objectArgTypes = {
  obj: Types.typedDict({}),
  key: {
    type: 'union' as const,
    members: ['none' as const, 'string' as const],
  },
};

export const opPick = makeObjectOp({
  name: 'pick',
  argTypes: objectArgTypes,
  renderInfo: {type: 'brackets'},
  returnType: (inputTypes, inputs) => {
    const {obj: objType} = inputTypes;
    const {key} = inputs;
    if (key.nodeType !== 'const') {
      // Should probably use unknown for cases like this.
      if (Types.isDict(objType)) {
        return Types.maybe(
          (objType as any) /* weird because we already think its a typedDict*/
            .objectType
        );
      } else if (Types.isTypedDict(objType)) {
        return Types.maybe(
          Types.union(Object.values(objType.propertyTypes).filter(notEmpty))
        );
      }
      throw new Error('invalid');
    }
    const keyValue = key.val;

    const subKeys = splitEscapedString(keyValue);

    return OpTypes.typedDictPathType(objType, subKeys);
  },
  description: `Selects a value from a ${Graph.docType('typedDict')} by key`,
  argDescriptions: {
    obj: `The input ${Graph.docType('typedDict')}`,
    key: `The key for the value to select`,
  },
  returnValueDescription: `Value at the given key`,
  resolver: async inputs => {
    if (inputs.key == null) {
      // This shouldn't happen because we're a standardOp, but we have an
      // incorrect type here.
      return null;
    }
    const subKeys = splitEscapedString(inputs.key);
    return OpTypes.typedDictPathVal(inputs.obj, subKeys);
  },
});

export const opValues = Graph.makeOp({
  hidden: true,
  name: 'typedDict-values',
  argTypes: {
    obj: Types.maybe(Types.oneOrMany(Types.maybe(Types.typedDict({})))),
  },
  returnType: inputs => {
    const {obj} = inputs;
    const objType = obj.type as Types.Type;

    return OpTypes.mappableNullableTaggable(objType, t => {
      const propertyTypes = Object.values(Types.typedDictPropertyTypes(t));
      if (propertyTypes.length === 0) {
        return Types.list('invalid');
      }
      return Types.list(Types.union(propertyTypes));
    });
  },
  description: `Returns the values of all the keys in a ${Graph.docType(
    'typedDict'
  )}`,
  argDescriptions: {
    obj: `The input ${Graph.docType('typedDict')}`,
  },
  returnValueDescription: `List of the ${Graph.docType('typedDict')} values`,
  resolver: async inputs => {
    return OpTypes.mappableNullableTaggableVal(inputs.obj, t => {
      return Object.values(t);
    });
  },
});

// Not yet public, needs generic implementation
export const opObjectKeyTypes = makeObjectOp({
  hidden: true,
  name: 'object-keytypes',
  argTypes: {
    obj: {type: 'typedDict', propertyTypes: {}},
  },
  returnType: ({obj}) =>
    Types.list(Types.typedDict({key: 'string', type: 'type'})),
  description: `Returns a ${Graph.docType(
    'typedDict'
  )} of the key types of the input ${Graph.docType('typedDict')}`,
  argDescriptions: {
    obj: `The input ${Graph.docType('typedDict')}`,
  },
  returnValueDescription: `${Graph.docType(
    'typedDict'
  )} with same keys as the input, and values corresponding the the ${Graph.docType(
    'type'
  )}.`,
  resolver: async (
    {obj},
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const objType = forwardOp.outputNode.node.fromOp.inputs.obj.type;
    return _.map(Types.typedDictPropertyTypes(objType), (type, key) => ({
      key,
      type,
    }));
  },
});

const extractTaggedPropertyTypes = (
  type: Types.TaggedValueType<Types.TypedDictType> | Types.TypedDictType
): Types.TypedDictType['propertyTypes'] => {
  let pTypes: Types.TypedDictType['propertyTypes'] = {};
  if (Types.isTaggedValue(type)) {
    const dict = type.value as Types.TypedDictType;
    pTypes = _.fromPairs(
      Object.keys(dict.propertyTypes).map(k => {
        return [
          k,
          Types.taggedValue(type.tag, dict.propertyTypes[k] as Types.Type),
        ];
      })
    );
  } else if (Types.isTypedDict(type)) {
    pTypes = type.propertyTypes;
  }
  return pTypes;
};

const extractConcreteProperties = (obj: any): any => {
  if (Types.isConcreteTaggedValue(obj)) {
    const dict = obj._value as any;
    const tag = obj._tag as any;
    obj = _.fromPairs(
      Object.keys(dict).map(k => {
        return [k, Types.concreteTaggedValue(tag, dict[k])];
      })
    );
  }
  return obj;
};

// Not yet public, needs generic implementation
// We call this from tableState.ts but only in the code path
// used by PanelPlot, which is not yet exposed
export const opMerge = Graph.makeOp({
  description: `Merges two ${Graph.docType('typedDict', {plural: true})}`,
  argDescriptions: {
    lhs: `The base ${Graph.docType('typedDict')}`,
    rhs: `The ${Graph.docType('typedDict')} to merge into the base`,
  },
  returnValueDescription: `A new ${Graph.docType(
    'typedDict'
  )} with the values from both inputs`,
  hidden: true,
  name: 'merge',
  argTypes: {
    lhs: {type: 'typedDict', propertyTypes: {}},
    rhs: {type: 'typedDict', propertyTypes: {}},
  },
  returnType: inputs => {
    return {
      type: 'typedDict',
      propertyTypes: {
        ...extractTaggedPropertyTypes(inputs.lhs.type),
        ...extractTaggedPropertyTypes(inputs.rhs.type),
      },
    };
  },
  resolver: inputs => {
    return {
      ...extractConcreteProperties(inputs.lhs),
      ...extractConcreteProperties(inputs.rhs),
    };
  },
});
